/**
 * DatePicker — the single date-input control used everywhere in the apps.
 *
 * The interface is intentionally thin and implementation-agnostic (value is an
 * ISO 'YYYY-MM-DD' string, onChange emits the same) so the internals can be
 * swapped — native <input type="date">, a library, or this custom calendar —
 * in ONE place without touching call sites. Ranges are just two DatePickers
 * (from with max=to, to with min=from), mirroring the native usage.
 *
 * Current impl: a dependency-free, dark-theme calendar popover.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './DatePicker.css';

export interface DatePickerProps {
  /** Selected date as ISO 'YYYY-MM-DD', or '' / undefined when empty. */
  value?: string;
  /** Emits ISO 'YYYY-MM-DD' on selection. */
  onChange: (iso: string) => void;
  /** Inclusive bounds as ISO 'YYYY-MM-DD'. */
  min?: string;
  max?: string;
  placeholder?: string;
  ariaLabel?: string;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  /**
   * Display format for the selected date, using tokens YYYY YY MMM MM M DD D
   * (e.g. 'D MMM YYYY' → "9 Jul 2026", 'MMM D, YYYY', 'DD/MM/YYYY').
   * When omitted, the default is smart: 'D MMM' for a date in the current year
   * ("9 Jul") and 'D MMM YYYY' otherwise ("9 Jul 2025"). Pass an explicit
   * format to always show it your way.
   */
  format?: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const pad = (n: number) => String(n).padStart(2, '0');
const toISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** Parse 'YYYY-MM-DD' as a LOCAL date (avoids the UTC shift of new Date(iso)). */
const fromISO = (s?: string): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const DEFAULT_FORMAT = 'D MMM YYYY';
/** Format a date via tokens: YYYY YY MMM MM M DD D (longest match wins). */
const fmt = (d: Date, f: string) => {
  const M = d.getMonth();
  const tok: Record<string, string> = {
    YYYY: String(d.getFullYear()),
    YY: pad(d.getFullYear() % 100),
    MMM: MONTHS[M],
    MM: pad(M + 1),
    M: String(M + 1),
    DD: pad(d.getDate()),
    D: String(d.getDate()),
  };
  return f.replace(/YYYY|MMM|MM|YY|DD|M|D/g, t => tok[t]);
};

export function DatePicker({
  value, onChange, min, max, placeholder = 'Select date',
  ariaLabel, title, className, style, disabled, format,
}: DatePickerProps) {
  const selected = fromISO(value);
  // Smart default: drop the year for current-year dates; keep it otherwise.
  const displayFormat = format
    ?? (selected && selected.getFullYear() === new Date().getFullYear() ? 'D MMM' : DEFAULT_FORMAT);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<Date>(() => selected ?? new Date());
  const ref = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [popStyle, setPopStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });

  // Snap the visible month to the selection each time the popover opens.
  useEffect(() => { if (open) setView(fromISO(value) ?? new Date()); }, [open, value]);

  /**
   * Position the popover against the viewport rather than the trigger.
   *
   * It used to be `position: absolute` under the trigger, which put it inside
   * whatever scroll container the field sat in — so on a long table the calendar
   * for the lower rows was clipped by the container's edge and those rows could
   * not be given a date at all. Fixed coordinates escape any `overflow` ancestor,
   * and the popover flips above the field when there is more room there.
   */
  useLayoutEffect(() => {
    if (!open) return;

    const place = () => {
      const trigger = ref.current?.getBoundingClientRect();
      if (!trigger) return;
      // Measure the popover when it exists; fall back to its designed height on
      // the first pass so it is never placed off-screen while unmeasured.
      const popHeight = popRef.current?.offsetHeight || 300;
      const popWidth = popRef.current?.offsetWidth || 252;
      const GAP = 6;
      const MARGIN = 8;

      const below = window.innerHeight - trigger.bottom;
      const above = trigger.top;
      // Prefer below; flip only when it does not fit AND above is roomier.
      const flip = below < popHeight + GAP + MARGIN && above > below;

      const top = flip
        ? Math.max(MARGIN, trigger.top - popHeight - GAP)
        : Math.min(trigger.bottom + GAP, window.innerHeight - popHeight - MARGIN);
      const left = Math.min(
        Math.max(MARGIN, trigger.left),
        Math.max(MARGIN, window.innerWidth - popWidth - MARGIN),
      );

      setPopStyle({ position: 'fixed', top, left, visibility: 'visible' });
    };

    place();
    // The trigger moves with its container, so track both. Capture phase to
    // catch scrolls on inner containers, not just the window.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // Re-hide on close so the next open cannot flash at the previous position.
  useEffect(() => { if (!open) setPopStyle({ visibility: 'hidden' }); }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const minD = fromISO(min);
  const maxD = fromISO(max);
  const inRange = (d: Date) => (!minD || d >= minD) && (!maxD || d <= maxD);

  const y = view.getFullYear();
  const m = view.getMonth();
  const days = useMemo(() => {
    const startOffset = new Date(y, m, 1).getDay(); // 0=Sun
    const gridStart = new Date(y, m, 1 - startOffset);
    return Array.from({ length: 42 }, (_, i) =>
      new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));
  }, [y, m]);
  const todayISO = toISO(new Date());

  const pick = (d: Date) => { onChange(toISO(d)); setOpen(false); };

  return (
    <div className={`sui-dp ${className ?? ''}`} style={style} ref={ref}>
      <button
        type="button"
        className="sui-dp-trigger"
        title={title}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
      >
        <span className={selected ? 'sui-dp-val' : 'sui-dp-ph'}>{selected ? fmt(selected, displayFormat) : placeholder}</span>
        <svg className="sui-dp-cal" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>
      {open && (
        <div className="sui-dp-pop" role="dialog" aria-label={ariaLabel} ref={popRef} style={popStyle}>
          <div className="sui-dp-head">
            <button type="button" className="sui-dp-nav" onClick={() => setView(new Date(y, m - 1, 1))} aria-label="Previous month">‹</button>
            <span className="sui-dp-title">{MONTHS[m]} {y}</span>
            <button type="button" className="sui-dp-nav" onClick={() => setView(new Date(y, m + 1, 1))} aria-label="Next month">›</button>
          </div>
          <div className="sui-dp-weekdays">{WEEKDAYS.map(w => <span key={w}>{w}</span>)}</div>
          <div className="sui-dp-grid">
            {days.map((d) => {
              const iso = toISO(d);
              const cls = [
                'sui-dp-day',
                d.getMonth() !== m ? 'sui-dp-day--out' : '',
                iso === value ? 'sui-dp-day--sel' : '',
                iso === todayISO ? 'sui-dp-day--today' : '',
              ].filter(Boolean).join(' ');
              return (
                <button key={iso} type="button" className={cls} disabled={!inRange(d)} onClick={() => pick(d)}>
                  {d.getDate()}
                </button>
              );
            })}
          </div>
          {value && (
            <button type="button" className="sui-dp-clear" onClick={() => { onChange(''); setOpen(false); }}>Clear</button>
          )}
        </div>
      )}
    </div>
  );
}
